import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useDisableScroll from '@/shared/hooks/useDisableScroll';
import { Button } from '@/shared/ui/Button';
import { useAppSelector } from '@/app/hooks';
import { selectUser } from '@/entities/user/model/userSlice';
import { getNews } from '@/entities/news';
import { userNewsApi } from '@/api/endpoints/usernews';
import { ME_QUERY_KEY } from '@/features/auth/api';
import { NewsItem } from './NewsItem';

// Триггер: при me.news > 0 тянем все непрочитанные через GET /usernews
// (UserNewsManager::unread() на беке) и показываем модалку. {t('news:acknowledge')}
// явно отмечает все показанные id через POST /usernews/read (запись в
// таблицу user_news) + invalidate ['me'] для обновления badge.
//
// Раньше использовали /site/news?page=1 + неявный bump last_read_news —
// неправильно: модалка показывала максимум 5 (бек pageSize хардкод), и
// per-news read-трекинг не работал. Сейчас матчится с легаси-флоу через
// отдельный /usernews endpoint.

export const NewsModal = () => {
  const { t } = useTranslation(['news', 'nav']);
  const user = useAppSelector(selectUser);
  const hasUnreadNews = (user?.news ?? 0) > 0;
  const queryClient = useQueryClient();
  const [active, setActive] = useState(false);

  const { data: newsItems } = useQuery({
    queryKey: ['news', 'unread'],
    queryFn: getNews,
    enabled: hasUnreadNews,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (newsItems && newsItems.length > 0) setActive(true);
  }, [newsItems]);

  useDisableScroll(active);

  const readMutation = useMutation({
    // Бэк (UsernewsController::actionRead) теперь сам бампит user.last_read_news
    // вместе с записью в user_news (known-server-issues #15 fixed) — отдельный
    // siteApi.news(1) для гашения бэджа колокольчика больше не нужен.
    mutationFn: (ids: number[]) => userNewsApi.read({ news_ids: ids }),
    onSettled: () => {
      setActive(false);
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['news', 'unread'] });
    },
  });

  const handleNewsReadClick = () => {
    if (!newsItems || newsItems.length === 0) {
      setActive(false);
      return;
    }
    readMutation.mutate(newsItems.map((n) => n.id));
  };

  if (!active || !newsItems) return null;

  return (
    <div className="fixed left-0 top-0 z-50 h-full w-full">
      <div className="h-full w-full bg-overlay-strong"></div>
      <div className="absolute-center m-auto flex h-[90vh] max-h-[580px] w-full max-w-[736px] flex-col bg-surface-page-alt py-4 pl-6 pr-3 md:max-h-[748px] md:rounded-md md:py-6 md:pl-8 md:pr-5">
        <h2 className="text-[22px] font-bold leading-[30px]">{t('nav:news')}</h2>
        <div className="flex h-[calc(100%-30px)] flex-col">
          <div className="scroll-transparent mt-2 flex h-full flex-1 flex-col overflow-scroll overflow-x-hidden pr-2 sm:mt-4">
            <div className="flex flex-1 flex-col gap-2 md:gap-4">
              {newsItems.map((item) => (
                <NewsItem key={item.id} item={item} />
              ))}
            </div>
            <Button
              className="mr-3 mt-2 h-[42px] !px-0 sm:ml-auto sm:mt-4 sm:w-[152px]"
              isLoading={readMutation.isPending}
              onClick={handleNewsReadClick}
            >
              {t('news:acknowledge')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
